import { Link } from "react-router-dom";
import { ChevronLeft, X, Check, Minus } from "lucide-react";
import { useCompare } from "../context/CompareContext";
import { formatPrice } from "../lib/api";
import Seo from "../components/Seo";

const SPECS = [
  { key: "year", label: "Year" },
  { key: "price", label: "Price", fmt: (v, l) => l.listing_type === "rental" ? (l.daily_price ? `$${l.daily_price}/day` : "—") : (v ? formatPrice(v) : "—") },
  { key: "body_type", label: "Body type" },
  { key: "fuel_type", label: "Fuel" },
  { key: "transmission", label: "Transmission" },
  { key: "engine_size", label: "Engine" },
  { key: "mileage", label: "Mileage", fmt: (v) => v != null ? `${Number(v).toLocaleString()} km` : "—" },
  { key: "condition", label: "Condition" },
  { key: "colour", label: "Colour" },
  { key: "location", label: "Location" },
];

export default function Compare() {
  const { items, remove, clear } = useCompare();

  if (items.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-20 text-center">
        <Seo title="Compare cars · Car Market" description="Side-by-side comparison of cars on Car Market" />
        <h1 className="font-display font-extrabold text-2xl text-cm-primary">No cars to compare yet</h1>
        <p className="text-sm text-slate-500 mt-2">Tap the <strong>compare</strong> icon on a listing to add it here.</p>
        <Link to="/buy" data-testid="compare-empty-browse" className="inline-flex items-center gap-2 mt-6 px-5 py-3 rounded-xl bg-cm-primary hover:bg-cm-accent text-white text-sm font-semibold press-scale">
          <ChevronLeft className="w-4 h-4" /> Browse cars
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="compare-page" className="bg-slate-50 min-h-screen">
      <Seo title={`Compare ${items.length} cars · Car Market`} description="Side-by-side comparison of cars you're considering" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/buy" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-cm-primary">
              <ChevronLeft className="w-4 h-4" /> Back to listings
            </Link>
            <h1 className="mt-2 font-display font-extrabold text-2xl sm:text-3xl text-cm-primary">Compare {items.length} cars</h1>
          </div>
          <button onClick={clear} data-testid="compare-page-clear" className="text-sm text-cm-accent hover:underline">Clear all</button>
        </div>

        {/* Card thumbs */}
        <div className={`grid gap-4 mb-6`} style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
          {items.map((it) => (
            <div key={it.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <div className="relative aspect-[16/10] bg-slate-100">
                {it.images?.[0] && <img src={it.images[0]} alt="" className="w-full h-full object-cover" />}
                <button onClick={() => remove(it.id)} aria-label="Remove" data-testid={`compare-page-remove-${it.id}`} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 text-slate-700 flex items-center justify-center shadow-md hover:bg-cm-accent hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-3 sm:p-4">
                <Link to={`/listing/${it.id}`} className="font-display font-bold text-cm-primary text-sm sm:text-base leading-tight line-clamp-2 hover:underline">
                  {it.year} {it.brand} {it.model}
                </Link>
                {it.variant && <p className="text-xs text-slate-500 mt-1 truncate">{it.variant}</p>}
                <div className="mt-2 font-display font-extrabold text-cm-primary text-base sm:text-lg">
                  {it.listing_type === "rental" ? (it.daily_price ? `$${it.daily_price}/day` : "—") : (it.price ? formatPrice(it.price) : "—")}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Spec matrix */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <tbody>
              {SPECS.map((s, i) => {
                const values = items.map((it) => s.fmt ? s.fmt(it[s.key], it) : (it[s.key] ?? "—"));
                const allSame = values.every((v) => v === values[0]);
                return (
                  <tr key={s.key} className={i % 2 ? "bg-slate-50/50" : ""}>
                    <td className="px-4 py-3 font-semibold text-slate-500 text-xs uppercase tracking-wider w-[140px] whitespace-nowrap">{s.label}</td>
                    {values.map((v, idx) => (
                      <td key={idx} className={`px-4 py-3 ${allSame ? 'text-slate-700' : 'text-cm-primary font-semibold'}`}>
                        {v === "—" ? <Minus className="w-3.5 h-3.5 text-slate-300 inline" /> : v}
                        {!allSame && v !== "—" && idx === values.findIndex((x) => x === v) && (
                          <Check className="inline-block w-3.5 h-3.5 text-emerald-500 ml-1" />
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className={`grid gap-3 mt-6`} style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
          {items.map((it) => (
            <Link key={it.id} to={`/listing/${it.id}`} data-testid={`compare-view-${it.id}`} className="py-3 rounded-xl bg-cm-primary hover:bg-cm-accent text-white text-center text-sm font-semibold press-scale">
              View details
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
